import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, Heart, MessageCircle, Award, Users, ExternalLink } from "lucide-react";
import PageHero from "../components/PageHero";
import { fetchCourses } from "../lib/api";
import { SCHOOL } from "../lib/site";

export default function Corsi() {
  const [courses, setCourses] = useState([]);
  const [filter, setFilter] = useState("Tutte");

  useEffect(() => {
    fetchCourses().then(setCourses).catch(() => {});
  }, []);

  const categories = ["Tutte", ...Array.from(new Set(courses.map((c) => c.categoria)))];
  const filtered = filter === "Tutte" ? courses : courses.filter((c) => c.categoria === filter);

  const waLink = `https://api.whatsapp.com/send?phone=${SCHOOL.whatsapp.number.replace("+", "")}&text=${encodeURIComponent("Salve, vorrei informazioni sui corsi della Trinity Musical School")}`;

  return (
    <div data-testid="page-corsi">
      <PageHero
        eyebrow="Corsi tradizionali · Per principianti e avanzati"
        title="I nostri corsi,"
        italic="dalla propedeutica all'eccellenza."
        subtitle="Corsi di musica e spettacolo rivolti a bambini, ragazzi e adulti che desiderino imparare a suonare uno strumento, cantare o avvicinarsi alle arti performative. Tenuti da musicisti professionisti diplomati in conservatorio."
      />

      {/* Due percorsi: Tradizionali vs Trinity */}
      <section className="py-20 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#A3133A]/15 border border-[#A3133A]/15 mb-20">
          <div className="bg-[#FBFBF9] p-8 sm:p-10" data-testid="track-tradizionali">
            <Users size={26} className="text-[#A3133A] mb-5" />
            <div className="text-[10px] tracking-eyebrow text-[#A3133A] mb-2">Percorso 1</div>
            <h3 className="font-serif-display text-3xl text-[#1C1C1C] mb-3 leading-tight">Corsi Tradizionali</h3>
            <p className="text-sm text-[#5C5C5C] leading-relaxed mb-5">
              Per chi desidera imparare a suonare uno strumento, cantare o recitare. Lezioni
              individuali e collettive cucite sulle attitudini di ciascun allievo.
            </p>
            <span className="text-[12px] tracking-eyebrow text-[#A3133A] inline-flex items-center gap-2">
              Sei già qui <ArrowRight size={12} />
            </span>
          </div>
          <div className="bg-[#FBFBF9] p-8 sm:p-10" data-testid="track-trinity">
            <Award size={26} className="text-[#A3133A] mb-5" />
            <div className="text-[10px] tracking-eyebrow text-[#A3133A] mb-2">Percorso 2</div>
            <h3 className="font-serif-display text-3xl text-[#1C1C1C] mb-3 leading-tight">Corsi Trinity College London</h3>
            <p className="text-sm text-[#5C5C5C] leading-relaxed mb-5">
              Percorsi finalizzati al conseguimento delle certificazioni internazionali Trinity,
              valide come credito formativo all'Esame di Stato.
            </p>
            <Link
              to="/certificazioni"
              data-testid="track-trinity-link"
              className="text-[12px] tracking-eyebrow text-[#A3133A] inline-flex items-center gap-2 link-underline"
            >
              Scopri i corsi Trinity <ArrowRight size={12} />
            </Link>
          </div>
        </div>

        {/* Filtri categoria */}
        <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-4">L'offerta formativa</div>
        <h2 className="font-serif-display text-4xl sm:text-5xl text-[#1C1C1C] leading-tight mb-10">
          {courses.length} corsi attivi,<br />
          <em className="italic font-light">tra strumento, canto e teatro.</em>
        </h2>

        <div className="flex flex-wrap gap-2 mb-12" data-testid="course-filters">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              data-testid={`filter-${cat.replace(/\s+/g, "-").toLowerCase()}`}
              className={`text-[11px] tracking-eyebrow px-4 py-2 rounded-sm border transition ${
                filter === cat
                  ? "bg-[#A3133A] text-white border-[#A3133A]"
                  : "bg-transparent text-[#A3133A] border-[#A3133A]/30 hover:border-[#A3133A]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#A3133A]/15 border border-[#A3133A]/15 mb-24">
          {filtered.map((c) => (
            <article key={c.id} data-testid={`course-card-${c.id}`} className="bg-[#FBFBF9] p-8 flex flex-col">
              <div className="text-[10px] tracking-eyebrow text-[#A3133A] mb-3">{c.categoria}</div>
              <h3 className="font-serif-display text-3xl text-[#1C1C1C] mb-3 leading-tight">{c.nome}</h3>
              <p className="text-sm text-[#5C5C5C] leading-relaxed mb-5 flex-1">{c.descrizione}</p>
              <div className="border-t border-[#A3133A]/15 pt-4 space-y-2 text-[12px]">
                <div className="flex justify-between gap-3">
                  <span className="text-[#5C5C5C] tracking-wide shrink-0">Livelli</span>
                  <span className="text-[#1C1C1C] text-right">{c.livelli.join(" · ")}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-[#5C5C5C] tracking-wide shrink-0">Durata</span>
                  <span className="text-[#1C1C1C] text-right">{c.durata}</span>
                </div>
              </div>
              {c.link ? (
                <a
                  href={c.link}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`course-external-${c.id}`}
                  className="mt-6 text-[12px] tracking-eyebrow text-[#A3133A] inline-flex items-center gap-2 link-underline"
                >
                  Scopri il coro su trinityvocalhub.net <ExternalLink size={12} />
                </a>
              ) : (
                <Link
                  to={`/iscrizione?corso=${c.id}`}
                  data-testid={`course-enroll-${c.id}`}
                  className="mt-6 text-[12px] tracking-eyebrow text-[#A3133A] inline-flex items-center gap-2 link-underline"
                >
                  Iscriviti a questo corso <ArrowRight size={12} />
                </Link>
              )}
            </article>
          ))}
        </div>

        {/* Trinity Vocal Hub — presentazione Cori */}
        <div className="mb-24" data-testid="vocal-hub-section">
          <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-4">I Cori · Trinity Vocal Hub</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#1C1C1C] leading-tight mb-6">
            Un hub, due cori, <em className="italic font-light">una voce sola.</em>
          </h2>
          <p className="text-[#5C5C5C] leading-relaxed max-w-3xl mb-10">
            Trinity Vocal Hub riunisce i due cori di Amici per la Musica APS: dagli 8 anni in su
            c'è un palco per te. Audizioni sempre aperte, prima prova gratuita e senza impegno —
            nessuna esperienza richiesta, solo la voglia di cantare insieme.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#A3133A]/15 border border-[#A3133A]/15 mb-8">
            <div className="bg-[#FBFBF9] p-8 sm:p-10" data-testid="vocal-hub-lucincanto">
              <div className="text-[10px] tracking-eyebrow text-[#A3133A] mb-2">Voci Bianche · 8–20 anni · Musical Pop Choir</div>
              <h3 className="font-serif-display text-3xl text-[#1C1C1C] mb-3 leading-tight">LucinCanto</h3>
              <p className="text-sm text-[#5C5C5C] leading-relaxed mb-5">
                Il coro pop per bambini e ragazzi: energia da musical, armonie moderne e palchi veri.
                Due sezioni — Piccole Voci (8–13) e Giovani Armonie (14–20). Repertorio pop &amp; musical,
                concerti durante tutto l'anno.
              </p>
            </div>
            <div className="bg-[#FBFBF9] p-8 sm:p-10" data-testid="vocal-hub-apm">
              <div className="text-[10px] tracking-eyebrow text-[#A3133A] mb-2">Coro Polifonico · Adulti 18+ · by Amici per la Musica APS</div>
              <h3 className="font-serif-display text-3xl text-[#1C1C1C] mb-3 leading-tight">APM Pop Project</h3>
              <p className="text-sm text-[#5C5C5C] leading-relaxed mb-5">
                Il nuovo coro polifonico per adulti: i grandi successi del pop riarrangiati a più voci
                — soprani, contralti, tenori e bassi. Si parte da zero, insieme. Prove settimanali serali
                a Rovigo e Polesella.
              </p>
            </div>
          </div>
          <a
            href="https://www.trinityvocalhub.net"
            target="_blank"
            rel="noreferrer"
            data-testid="vocal-hub-cta"
            className="bg-[#A3133A] text-white px-8 py-4 text-[12px] tracking-eyebrow rounded-sm hover:bg-[#8B0F2F] transition inline-flex items-center gap-2"
          >
            Scopri i cori su trinityvocalhub.net <ExternalLink size={14} />
          </a>
        </div>

        {/* Banner Inclusione - BES / DSA */}
        <div className="bg-[#A3133A] text-[#FBFBF9] p-10 md:p-16 relative overflow-hidden grain rounded-sm grid grid-cols-1 lg:grid-cols-12 gap-10 items-center" data-testid="inclusion-banner">
          <div className="lg:col-span-2 flex justify-center lg:justify-start relative z-10">
            <Heart size={56} className="opacity-95" />
          </div>
          <div className="lg:col-span-10 relative z-10">
            <div className="text-[11px] tracking-eyebrow opacity-80 mb-3">Inclusione · DSA · BES</div>
            <h3 className="font-serif-display text-3xl sm:text-4xl leading-tight mb-5">
              Una scuola per <em className="italic font-light">tutti</em>.
            </h3>
            <p className="opacity-95 leading-relaxed text-base sm:text-lg max-w-3xl">
              Le nostre attività sono rivolte anche — e soprattutto — a ragazzi con
              <strong> disturbi specifici dell'apprendimento</strong>. Nella scuola è presente
              personale abilitato a favorire l'inclusione e l'apprendimento di studenti con
              <strong> disabilità o Bisogni Educativi Speciali (BES)</strong>.
            </p>
          </div>
        </div>

        {/* CTA WhatsApp + telefoni */}
        <div className="mt-20 text-center">
          <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-3">Lezione di prova</div>
          <h3 className="font-serif-display text-4xl sm:text-5xl text-[#1C1C1C] mb-6 leading-tight">
            Vuoi <em className="italic font-light">provare</em>?
          </h3>
          <p className="text-[#5C5C5C] max-w-2xl mx-auto mb-8 leading-relaxed">
            Contattaci per fissare una lezione di prova o ricevere informazioni sui corsi.
            Ti risponderemo direttamente da WhatsApp.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href={waLink}
              target="_blank"
              rel="noreferrer"
              data-testid="corsi-cta-whatsapp"
              className="bg-[#25D366] text-white px-8 py-4 text-[12px] tracking-eyebrow rounded-sm hover:bg-[#1FAE54] transition inline-flex items-center gap-2"
            >
              <MessageCircle size={14} /> Scrivi su WhatsApp
            </a>
            <Link
              to="/iscrizione"
              data-testid="corsi-cta-iscriviti"
              className="border border-[#A3133A] text-[#A3133A] px-8 py-4 text-[12px] tracking-eyebrow rounded-sm hover:bg-[#A3133A] hover:text-white transition inline-flex items-center gap-2"
            >
              <GraduationCap size={14} /> Iscriviti online
            </Link>
          </div>
          <div className="mt-8 text-sm text-[#5C5C5C]">
            Oppure chiama: {SCHOOL.phones.map((p) => p.value).join(" · ")}
          </div>
        </div>
      </section>
    </div>
  );
}
